import type { ReactNode } from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Search } from "lucide-react"

interface Column<T> {
  key?: keyof T
  label: string
  header?: string
  accessorKey?: keyof T | string
  cell?: (props: { row: { original: T } }) => ReactNode
  render?: (value: any, item: T) => ReactNode
}

interface DataTableProps<T> {
  title?: string
  description?: string
  data: T[]
  columns: Column<T>[]
  searchable?: boolean
  searchPlaceholder?: string
  onSearch?: (query: string) => void
  actions?: ReactNode
  emptyMessage?: string
}

export function DataTable<T extends Record<string, any>>({
  title,
  description,
  data,
  columns,
  searchable = false,
  searchPlaceholder = "Search...",
  onSearch,
  actions,
  emptyMessage = "No data available",
}: DataTableProps<T>) {
  return (
    <Card>
      {(title || description || searchable || actions) && (
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              {title && <CardTitle>{title}</CardTitle>}
              {description && <CardDescription>{description}</CardDescription>}
            </div>
            <div className="flex items-center gap-2">
              {searchable && (
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={searchPlaceholder}
                    className="pl-8 w-64"
                    onChange={(e) => onSearch?.(e.target.value)}
                  />
                </div>
              )}
              {actions}
            </div>
          </div>
        </CardHeader>
      )}
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column, index) => (
                  <TableHead key={column.key ? String(column.key) : `header-${index}`}>
                    {column.header || column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, index) => (
                  <TableRow key={index}>
                    {columns.map((column, colIndex) => (
                      <TableCell key={column.key ? String(column.key) : `cell-${index}-${colIndex}`}>
                        {column.cell 
                          ? column.cell({ row: { original: item } })
                          : column.render 
                            ? column.render(item[column.key!], item)
                            : String(item[column.accessorKey || column.key!] || "-")
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
